import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  addDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Users,
  Search,
  MoreVertical,
  Shield,
  UserCheck,
  UserX,
  Loader2,
  Mail,
  Phone,
  Bell,
  Eye,
  Wallet,
  Calendar,
  CheckCircle2,
  XCircle,
  Filter,
  BarChart3,
  MailQuestion,
  Trash2,
  History,
  Clock,
  Target,
  Download
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { logAction } from '../../lib/audit';
import { monthlyBreakdown, MONTH_LABELS, isEventContribution } from '../../lib/contributions';
import { exportMembersToExcel } from '../../lib/membersExcel';

// Catégories Sass par défaut (utilisées si non configurées dans les Réglages)
const DEFAULT_SASS_CATEGORIES = [
  { id: 'eleve', label: 'Élève', amount: 25000 },
  { id: 'etudiant', label: 'Étudiant(e)', amount: 35000 },
  { id: 'travailleur', label: 'Travailleur(se)', amount: 50000 }
];

export default function Members() {
  const { isAdmin, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');

  // Member Details State
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberContributions, setMemberContributions] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Notification State
  const [notifDialog, setNotifDialog] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifTitle, setNotifTitle] = useState('Note de l\'administration');
  const [sendingNotif, setSendingNotif] = useState(false);
  const [settings, setSettings] = useState({ annualGoal: 100000 });
  const [memberToBan, setMemberToBan] = useState(null);
  const [banning, setBanning] = useState(false);

  // Sass Annuel (Objectif annuel personnalisé par membre)
  const [editingGoalDialog, setEditingGoalDialog] = useState(false);
  const [memberAnnualGoal, setMemberAnnualGoal] = useState('');
  const [selectedSassCategory, setSelectedSassCategory] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reminding, setReminding] = useState(false);

  // Catégories Sass configurées (Réglages) avec repli sur les valeurs par défaut
  const sassCategories = settings.sassCategories?.length ? settings.sassCategories : DEFAULT_SASS_CATEGORIES;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'settings', 'general'));
        if (docSnap.exists()) setSettings(docSnap.data());
      } catch (error) { console.error('Error fetching settings:', error); }
    };
    fetchSettings();
  }, []);

  const roles = [
    { value: 'membre', label: 'Membre' },
    { value: 'responsable_commission', label: 'Responsable Commission' },
    { value: 'admin', label: 'Administrateur' }
  ];

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'active', label: 'Actifs' },
    { value: 'inactive', label: 'Inactifs' }
  ];

  const approvalOptions = [
    { value: 'all', label: 'Tous' },
    { value: 'pending', label: 'En attente' },
    { value: 'approved', label: 'Validés' },
    { value: 'rejected', label: 'Refusés' }
  ];

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const membersQuery = query(collection(db, 'users'), orderBy('joinedAt', 'desc'));
      const snapshot = await getDocs(membersQuery);
      const membersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Fetch confirmed contributions for annual totals
      const contributionsQuery = query(
        collection(db, 'contributions'),
        where('status', '==', 'confirmed')
      );
      const contriSnapshot = await getDocs(contributionsQuery);
      const contributions = contriSnapshot.docs.map(doc => doc.data());

      const currentYear = new Date().getFullYear();
      const dataWithTotals = membersData.map(member => {
        // Cotisations régulières uniquement : les cotisations rattachées à un
        // événement (collectes ponctuelles) ne comptent ni dans le suivi
        // mensuel des cotisations, ni dans l'objectif annuel.
        const memberContribs = contributions.filter(
          c => c.userId === member.id && !isEventContribution(c)
        );
        const monthly = monthlyBreakdown(memberContribs, currentYear); // 12 cases Jan→Déc
        const annualTotal = monthly.reduce((sum, v) => sum + v, 0);
        return { ...member, annualTotal, objectiveTotal: annualTotal, monthly };
      });

      setMembers(dataWithTotals);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberDetails = async (member) => {
    setSelectedMember(member);
    setLoadingDetails(true);
    try {
      const q = query(
        collection(db, 'contributions'),
        where('userId', '==', member.id),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMemberContributions(data);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!selectedMember || !notifMessage) return;

    setSendingNotif(true);
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: selectedMember.id,
        title: notifTitle,
        message: notifMessage,
        type: 'member',
        read: false,
        createdAt: serverTimestamp()
      });
      toast.success('Notification envoyée avec succès');
      setNotifDialog(false);
      setNotifMessage('');
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingNotif(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', memberId), { role: newRole });
      const targetMember = members.find(m => m.id === memberId);
      await logAction(user.uid, user.email, 'UPDATE_ROLE', `Membre: ${targetMember?.fullName}`, { newRole });
      toast.success('Rôle mis à jour');
      fetchMembers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleToggleStatus = async (memberId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'users', memberId), { isActive: !currentStatus });
      toast.success(currentStatus ? 'Membre désactivé' : 'Membre activé');
      fetchMembers();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Approve or reject a member
  const handleApproval = async (memberId, approved, memberName) => {
    try {
      await updateDoc(doc(db, 'users', memberId), { isApproved: approved });
      await logAction(user.uid, user.email, approved ? 'APPROVE_MEMBER' : 'REJECT_MEMBER', `Membre: ${memberName}`);

      // Send notification to the member
      await addDoc(collection(db, 'notifications'), {
        userId: memberId,
        title: approved ? 'Bienvenue dans le Dahira !' : 'Demande d\'adhésion refusée',
        message: approved
          ? `Félicitations ${memberName} ! Votre compte a été validé par l'administration. Vous pouvez maintenant accéder à toutes les fonctionnalités du Dahira.`
          : `Nous sommes désolés, votre demande d'adhésion n'a pas été approuvée. Veuillez contacter l'administration pour plus d'informations.`,
        type: approved ? 'success' : 'warning',
        read: false,
        createdAt: serverTimestamp()
      });

      toast.success(approved ? 'Membre validé avec succès' : 'Membre rejeté');
      fetchMembers();
    } catch (error) {
      console.error('Error updating approval:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleBanMember = async () => {
    if (!memberToBan) return;
    setBanning(true);
    try {
      const binnedMember = memberToBan;
      await deleteDoc(doc(db, 'users', binnedMember.id));
      await logAction(user.uid, user.email, 'BAN_MEMBER', `Membre: ${binnedMember.fullName}`);
      toast.success('Membre banni du Dahira avec succès');
      fetchMembers();
    } catch (error) {
      console.error('Error banning member:', error);
      toast.error('Erreur lors du bannissement');
    } finally {
      setBanning(false);
      setMemberToBan(null);
    }
  };

  // Sauvegarder l'objectif annuel personnalisé du membre (Sass Annuel)
  const handleSaveAnnualGoal = async () => {
    if (!selectedMember) return;
    setSavingGoal(true);
    try {
      const goalValue = parseInt(memberAnnualGoal) || settings.annualGoal;
      await updateDoc(doc(db, 'users', selectedMember.id), {
        annualGoal: goalValue,
        sassCategory: selectedSassCategory || null
      });

      // Mettre à jour le membre sélectionné localement
      setSelectedMember(prev => ({ ...prev, annualGoal: goalValue, sassCategory: selectedSassCategory || null }));

      toast.success(`Sass Annuel de ${selectedMember.fullName} mis à jour: ${goalValue.toLocaleString()} FCFA`);
      setEditingGoalDialog(false);
      fetchMembers(); // Rafraîchir la liste
    } catch (error) {
      console.error('Error saving annual goal:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingGoal(false);
    }
  };

  // Ouvrir le dialog d'édition du Sass Annuel
  const openGoalEditor = (member) => {
    setSelectedMember(member);
    setMemberAnnualGoal(member.annualGoal || settings.annualGoal || 100000);
    setSelectedSassCategory(member.sassCategory || '');
    setEditingGoalDialog(true);
  };

  // Choisir une catégorie Sass : applique son montant comme objectif annuel
  const applySassCategory = (cat) => {
    setSelectedSassCategory(cat.id);
    setMemberAnnualGoal(cat.amount);
  };

  // Relancer les membres en retard de cotisation pour le mois en cours
  const handleRemindLate = async () => {
    const currentMonth = new Date().getMonth();
    const monthName = new Date(new Date().getFullYear(), currentMonth, 1).toLocaleDateString('fr-FR', { month: 'long' });
    const late = filteredMembers.filter(m => {
      if (m.role === 'admin') return false;
      if (m.isApproved !== true && m.role !== 'admin') return false; // seulement les membres validés
      const target = (m.annualGoal || settings.annualGoal || 100000) / 12;
      return (m.monthly?.[currentMonth] || 0) < target;
    });
    if (late.length === 0) {
      toast.info('Aucun membre en retard ce mois-ci 🎉');
      return;
    }
    setReminding(true);
    try {
      await Promise.all(late.map(m =>
        addDoc(collection(db, 'notifications'), {
          userId: m.id,
          title: 'Rappel de cotisation',
          message: `Assalamou aleykoum ${m.fullName?.split(' ')[0] || ''}. Votre cotisation du mois de ${monthName} n'a pas encore été enregistrée. Qu'Allah vous facilite. Jazakoumoullahou khayran.`,
          type: 'contribution',
          read: false,
          createdAt: serverTimestamp()
        })
      ));
      await logAction(user.uid, user.email, 'REMIND_LATE_MEMBERS', `Relance de ${late.length} membre(s) en retard (${monthName})`);
      toast.success(`${late.length} membre(s) en retard relancé(s)`);
    } catch (error) {
      console.error('Error reminding late members:', error);
      toast.error('Erreur lors de la relance');
    } finally {
      setReminding(false);
    }
  };

  // Exporter la liste des membres en Excel (classeur formaté : voir lib/membersExcel)
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const count = await exportMembersToExcel(filteredMembers, { settings, sassCategories });
      logAction(user.uid, user.email, 'EXPORT_MEMBERS', `Export Excel de ${count} membre(s)`);
      toast.success(`${count} membre(s) exporté(s) avec succès`);
    } catch (error) {
      console.error('Error exporting members:', error);
      toast.error('Erreur lors de l\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      const matchesSearch = !search ||
        m.fullName?.toLowerCase().includes(search.toLowerCase()) ||
        m.email?.toLowerCase().includes(search.toLowerCase()) ||
        m.phone?.includes(search);

      const matchesRole = roleFilter === 'all' || m.role === roleFilter;
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' ? m.isActive !== false : m.isActive === false);

      const isActuallyApproved = m.isApproved === true || m.isApproved === undefined;
      const isActuallyPending = m.isApproved === 'pending' || m.isApproved === false; // On traite false comme en attente s'il vient de l'ancien code, mais le nouveau code distinguera

      let matchesApproval = true;
      if (approvalFilter === 'pending') matchesApproval = m.isApproved === 'pending';
      else if (approvalFilter === 'approved') matchesApproval = isActuallyApproved;
      else if (approvalFilter === 'rejected') matchesApproval = m.isApproved === false;

      return matchesSearch && matchesRole && matchesStatus && matchesApproval;
    });
  }, [members, search, roleFilter, statusFilter, approvalFilter]);

  const stats = useMemo(() => {
    return {
      total: members.length,
      active: members.filter(m => m.isActive !== false).length,
      admins: members.filter(m => m.role === 'admin').length,
      pending: members.filter(m => m.isApproved === 'pending').length,
      newThisMonth: members.filter(m => {
        const date = m.joinedAt?.toDate?.() || new Date(m.joinedAt);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }).length
    };
  }, [members]);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-semibold mb-2">Accès restreint</h2>
        <p className="text-muted-foreground">Cette page est réservée aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Header with Stats */}
      <div className="relative overflow-hidden rounded-2xl md:rounded-[2.5rem] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiIGN4PSIyMCIgY3k9IjIwIiByPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-20" />

        <div className="relative z-10 flex flex-col gap-4 md:gap-8">
          <div>
            <h1 className="text-xl md:text-4xl font-black tracking-tight mb-1 md:mb-2">Membres</h1>
            <p className="text-slate-400 max-w-md text-sm md:text-base hidden md:block">Gérez les membres de la Daara et suivez leur engagement.</p>
          </div>

          <div className="grid grid-cols-5 gap-2 md:gap-4 w-full">
            <div className="p-2 md:p-4 rounded-xl md:rounded-3xl bg-white/5 backdrop-blur-md border border-white/10">
              <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5 md:mb-1">Total</p>
              <p className="text-lg md:text-2xl font-black">{stats.total}</p>
            </div>
            <div className="p-2 md:p-4 rounded-xl md:rounded-3xl bg-white/5 backdrop-blur-md border border-white/10">
              <p className="text-[8px] md:text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-0.5 md:mb-1">Actifs</p>
              <p className="text-lg md:text-2xl font-black">{stats.active}</p>
            </div>
            <div
              className={`p-2 md:p-4 rounded-xl md:rounded-3xl backdrop-blur-md border cursor-pointer transition-all ${stats.pending > 0
                ? 'bg-amber-500/20 border-amber-500/30 hover:bg-amber-500/30'
                : 'bg-white/5 border-white/10'
                }`}
              onClick={() => setApprovalFilter(approvalFilter === 'pending' ? 'all' : 'pending')}
            >
              <p className="text-[8px] md:text-[10px] font-bold text-amber-400 uppercase tracking-widest mb-0.5 md:mb-1">En attente</p>
              <p className="text-lg md:text-2xl font-black">{stats.pending}</p>
            </div>
            <div className="p-2 md:p-4 rounded-xl md:rounded-3xl bg-white/5 backdrop-blur-md border border-white/10">
              <p className="text-[8px] md:text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-0.5 md:mb-1">Nouveau</p>
              <p className="text-lg md:text-2xl font-black">+{stats.newThisMonth}</p>
            </div>
            <div className="p-2 md:p-4 rounded-xl md:rounded-3xl bg-white/5 backdrop-blur-md border border-white/10">
              <p className="text-[8px] md:text-[10px] font-bold text-purple-400 uppercase tracking-widest mb-0.5 md:mb-1">Admins</p>
              <p className="text-lg md:text-2xl font-black">{stats.admins}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Control Bar */}
      <Card className="border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-primary transition-colors" />
              <Input
                placeholder="Rechercher un membre par nom, email ou num..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12 rounded-2xl border-slate-200 focus:ring-primary/20 transition-all"
              />
            </div>
            <div className="flex gap-3">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full lg:w-48 h-12 rounded-2xl border-slate-200">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Rôle" />
                </SelectTrigger>
                <SelectContent rounded-2xl>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  {roles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full lg:w-44 h-12 rounded-2xl border-slate-200">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={approvalFilter} onValueChange={setApprovalFilter}>
                <SelectTrigger className={`w-full lg:w-48 h-12 rounded-2xl border-slate-200 ${approvalFilter === 'pending' ? 'ring-2 ring-amber-500 bg-amber-50' : ''}`}>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <SelectValue placeholder="Validation" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {approvalOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Button
                onClick={handleRemindLate}
                disabled={reminding || filteredMembers.length === 0}
                variant="outline"
                className="h-12 rounded-2xl border-amber-300 text-amber-600 hover:bg-amber-50 whitespace-nowrap"
              >
                {reminding ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Relancer les retards
              </Button>

              <Button
                onClick={handleExportExcel}
                disabled={exporting || filteredMembers.length === 0}
                className="h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20 whitespace-nowrap"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exporter Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card className="border-slate-200 dark:border-slate-800 rounded-2xl md:rounded-[2rem] overflow-hidden shadow-xl bg-white/50 backdrop-blur-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 md:py-24">
              <Loader2 className="h-10 w-10 md:h-12 md:w-12 animate-spin text-primary mb-4" />
              <p className="text-slate-500 font-medium tracking-tight text-sm">Synchronisation...</p>
            </div>
          ) : filteredMembers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50/50 dark:bg-slate-900/50">
                  <TableRow className="hover:bg-transparent border-0">
                    <TableHead className="py-3 md:py-5 px-3 md:px-6 font-bold text-slate-500 whitespace-nowrap">Membre</TableHead>
                    <TableHead className="font-bold text-slate-500 hidden md:table-cell">Contact</TableHead>
                    <TableHead className="font-bold text-slate-500 hidden lg:table-cell">Cotisation</TableHead>
                    <TableHead className="font-bold text-slate-500 whitespace-nowrap">Rôle</TableHead>
                    <TableHead className="font-bold text-slate-500 whitespace-nowrap">Statut</TableHead>
                    <TableHead className="w-10 md:w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member) => (
                    <TableRow
                      key={member.id}
                      className="group cursor-pointer hover:bg-slate-50/80 dark:hover:bg-slate-900/40 transition-colors border-slate-100 dark:border-slate-800"
                      onClick={() => fetchMemberDetails(member)}
                    >
                      <TableCell className="py-3 md:py-4 px-3 md:px-6">
                        <div className="flex items-center gap-2 md:gap-4">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-[1.2rem] bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center shrink-0 border border-primary/10">
                            <span className="text-primary font-black text-base md:text-lg">
                              {member.fullName?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-slate-900 dark:text-slate-100 truncate text-sm md:text-base">{member.fullName}</p>
                            <p className="text-xs text-slate-500 md:hidden truncate">{member.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <Mail className="h-3.5 w-3.5 opacity-50" />
                            <span className="truncate max-w-[180px]">{member.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <Phone className="h-3.5 w-3.5 opacity-50" />
                            {member.phone || 'N/A'}
                          </div>
                          {member.joinedAt && (
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <Calendar className="h-3 w-3 opacity-50" />
                              Membre depuis {new Date(member.joinedAt?.toDate?.() || member.joinedAt).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="space-y-1.5 min-w-[140px]">
                          <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            <span>{Math.round(Math.min((member.objectiveTotal || 0) / ((member.annualGoal || settings.annualGoal || 100000) / 100), 100))}%</span>
                            <span>{member.objectiveTotal?.toLocaleString() || 0} / {(member.annualGoal || settings.annualGoal || 100000).toLocaleString()}</span>
                          </div>
                          <Progress value={Math.min((member.objectiveTotal || 0) / ((member.annualGoal || settings.annualGoal || 100000) / 100), 100)} className="h-1.5 shadow-sm" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`
                          rounded-lg md:rounded-xl px-2 md:px-3 py-0.5 md:py-1 font-bold text-[10px] md:text-[11px] uppercase tracking-wider
                          ${member.role === 'admin' ? 'bg-purple-500 hover:bg-purple-600' :
                            member.role === 'responsable_commission' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-slate-500 hover:bg-slate-600'}
                        `}>
                          {member.role === 'admin' ? 'Admin' : member.role === 'responsable_commission' ? 'Resp.' : 'Membre'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {member.isApproved === 'pending' ? (
                          <div className="flex items-center gap-1 md:gap-2 text-amber-600 font-bold text-xs md:text-sm whitespace-nowrap">
                            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                            <span className="hidden md:inline">En attente</span>
                          </div>
                        ) : member.isApproved === false ? (
                          <div className="flex items-center gap-1 md:gap-2 text-red-500 font-bold text-xs md:text-sm whitespace-nowrap">
                            <div className="w-2 h-2 rounded-full bg-red-400" />
                            <span className="hidden md:inline">Refusé</span>
                          </div>
                        ) : member.isActive !== false ? (
                          <div className="flex items-center gap-1 md:gap-2 text-emerald-600 font-bold text-xs md:text-sm whitespace-nowrap">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="hidden md:inline">Actif</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 md:gap-2 text-slate-400 font-bold text-xs md:text-sm whitespace-nowrap">
                            <div className="w-2 h-2 rounded-full bg-slate-300" />
                            <span className="hidden md:inline">Inactif</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10 rounded-xl hover:bg-white shadow-sm ring-1 ring-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => e.stopPropagation()} // Empêcher l'ouverture du profil lors du clic sur le menu
                            >
                              <MoreVertical className="h-5 w-5 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56 rounded-2xl p-2 shadow-2xl border-slate-200">
                            <DropdownMenuLabel className="text-[10px] uppercase font-black text-slate-400 px-3 py-2">Action Rapide</DropdownMenuLabel>
                            <DropdownMenuItem
                              className="rounded-xl py-2.5 focus:bg-slate-100"
                              onClick={() => fetchMemberDetails(member)}
                            >
                              <Eye className="h-4 w-4 mr-3 text-slate-400" /> Consulter le profil
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="rounded-xl py-2.5 focus:bg-slate-100"
                              onClick={() => { setSelectedMember(member); setNotifDialog(true); }}
                            >
                              <Bell className="h-4 w-4 mr-3 text-primary" /> Envoyer une alerte
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="rounded-xl py-2.5 focus:bg-emerald-50 text-emerald-700"
                              onClick={(e) => { e.stopPropagation(); openGoalEditor(member); }}
                            >
                              <Target className="h-4 w-4 mr-3 text-emerald-600" /> Modifier Sass Annuel
                            </DropdownMenuItem>

                            <DropdownMenuSeparator className="my-2" />
                            <DropdownMenuLabel className="text-[10px] uppercase font-black text-slate-400 px-3 py-2">Gestion Rôle</DropdownMenuLabel>
                            {roles.map((role) => (
                              <DropdownMenuItem
                                key={role.value}
                                className={`rounded-xl py-2.5 focus:bg-slate-100 ${member.role === role.value ? 'bg-slate-50 font-bold text-primary' : ''}`}
                                onClick={() => handleUpdateRole(member.id, role.value)}
                              >
                                {member.role === role.value && <div className="w-1.5 h-1.5 rounded-full bg-primary mr-2" />}
                                {role.label}
                              </DropdownMenuItem>
                            ))}

                            <DropdownMenuSeparator className="my-2" />
                            <DropdownMenuLabel className="text-[10px] uppercase font-black text-amber-500 px-3 py-2">Validation</DropdownMenuLabel>
                            {member.isApproved !== true ? (
                              <DropdownMenuItem
                                className="rounded-xl py-2.5 focus:bg-emerald-50 text-emerald-600 focus:text-emerald-600 font-bold"
                                onClick={(e) => { e.stopPropagation(); handleApproval(member.id, true, member.fullName); }}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-3" /> Valider le membre
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="rounded-xl py-2.5 focus:bg-red-50 text-red-600 focus:text-red-600 font-bold"
                                onClick={(e) => { e.stopPropagation(); handleApproval(member.id, false, member.fullName); }}
                              >
                                <XCircle className="h-4 w-4 mr-3" /> Révoquer/Refuser
                              </DropdownMenuItem>
                            )}

                            {member.isApproved === 'pending' && (
                              <DropdownMenuItem
                                className="rounded-xl py-2.5 focus:bg-red-50 text-red-600 focus:text-red-600 font-bold"
                                onClick={(e) => { e.stopPropagation(); handleApproval(member.id, false, member.fullName); }}
                              >
                                <XCircle className="h-4 w-4 mr-3" /> Rejeter la demande
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator className="my-2" />
                            <DropdownMenuItem
                              className="rounded-xl py-2.5 focus:bg-slate-100 font-bold"
                              onClick={() => handleToggleStatus(member.id, member.isActive !== false)}
                            >
                              {member.isActive !== false ?
                                <><UserX className="h-4 w-4 mr-3 text-red-500" /> Désactiver</> :
                                <><UserCheck className="h-4 w-4 mr-3 text-emerald-500" /> Réactiver</>}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="rounded-xl py-2.5 focus:bg-red-50 text-red-600 focus:text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMemberToBan(member);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-3" /> Bannir du Dahira
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-24 text-center">
              <div className="w-20 h-20 rounded-[2rem] bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Users className="h-10 w-10 text-slate-300 dark:text-slate-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Aucun membre trouvé</h3>
              <p className="text-slate-500 max-w-[300px] mx-auto mt-2 leading-relaxed">Ajustez vos filtres ou effectuez une nouvelle recherche pour trouver un membre spécifique.</p>
              <Button
                variant="outline"
                className="mt-8 rounded-2xl border-slate-200"
                onClick={() => { setSearch(''); setRoleFilter('all'); setStatusFilter('all'); setApprovalFilter('all'); }}
              >
                Réinitialiser les filtres
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MEMBER PROFILE DRAWER (MODAL) */}
      <Dialog open={!!selectedMember && !notifDialog} onOpenChange={(open) => !open && setSelectedMember(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden rounded-[2.5rem] border-0 shadow-2xl">
          {selectedMember && (
            <div className="flex flex-col max-h-[90vh]">
              {/* Profile Header */}
              <div className="relative p-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden shrink-0">
                <div className="absolute top-0 right-0 w-48 h-48 bg-primary/20 rounded-full blur-3xl -mr-10 -mt-10" />
                <div className="relative flex items-center gap-6">
                  <div className="w-24 h-24 rounded-[2rem] bg-white/10 backdrop-blur-md flex items-center justify-center border-2 border-white/20 shadow-2xl">
                    <span className="text-4xl font-black">{selectedMember.fullName?.charAt(0)}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-3xl font-black tracking-tight">{selectedMember.fullName}</h2>
                      <Badge className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm px-3 py-1 font-bold">
                        {selectedMember.role}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-slate-300 text-sm">
                      <span className="flex items-center gap-1.5"><Mail className="w-4 h-4" />{selectedMember.email}</span>
                      <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" />{selectedMember.phone || 'Non renseigné'}</span>
                      <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />Inscrit le {new Date(selectedMember.joinedAt?.toDate?.() || selectedMember.joinedAt).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Stats Bar */}
              <div className="flex divide-x divide-slate-100 dark:divide-slate-800 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
                <div className="flex-1 p-5 text-center">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Cotisations Totales</p>
                  <p className="text-2xl font-black text-primary">
                    {memberContributions.filter(c => c.status === 'confirmed').reduce((acc, c) => acc + (c.amount || 0), 0).toLocaleString()} F
                  </p>
                </div>
                <div className="flex-1 p-5 text-center">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Transactions</p>
                  <p className="text-2xl font-black">{memberContributions.length}</p>
                </div>
                <div className="flex-1 p-5 text-center">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Statut Actuel</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${selectedMember.isActive !== false ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-300'}`} />
                    <span className="text-lg font-bold">{selectedMember.isActive !== false ? 'Membre Actif' : 'Inactif'}</span>
                  </div>
                </div>
              </div>

              {/* Suivi mensuel (grille 12 mois) */}
              {(() => {
                const monthly = selectedMember.monthly || Array(12).fill(0);
                const goal = selectedMember.annualGoal || settings.annualGoal || 100000;
                const monthlyTarget = goal / 12;
                const currentMonth = new Date().getMonth();
                const paidMonths = monthly.filter((amt, i) => amt >= monthlyTarget).length;
                return (
                  <div className="border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shrink-0">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-primary" />
                        Suivi mensuel {new Date().getFullYear()}
                      </h3>
                      <span className="text-xs font-bold text-slate-400">{paidMonths}/12 mois à jour</span>
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {MONTH_LABELS.map((label, i) => {
                        const amt = monthly[i] || 0;
                        const status = amt >= monthlyTarget ? 'paid' : amt > 0 ? 'partial' : (i <= currentMonth ? 'late' : 'upcoming');
                        const styles = {
                          paid: 'bg-emerald-500 text-white border-emerald-500',
                          partial: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
                          late: 'bg-red-50 text-red-500 border-red-200 dark:bg-red-900/20 dark:text-red-300',
                          upcoming: 'bg-slate-50 text-slate-400 border-slate-200 dark:bg-slate-800 dark:text-slate-500'
                        }[status];
                        return (
                          <div key={i} className={`rounded-xl border p-2 text-center ${styles}`} title={`${amt.toLocaleString()} F`}>
                            <p className="text-[10px] font-black uppercase">{label}</p>
                            <p className="text-[10px] font-medium mt-0.5">{amt > 0 ? `${Math.round(amt / 1000)}k` : '—'}</p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-400 font-medium">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500" />À jour</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-300" />Partiel</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-200" />En retard</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-slate-200" />À venir</span>
                    </div>
                  </div>
                );
              })()}

              {/* History Content */}
              <div className="flex-1 overflow-auto p-8 bg-slate-50/50 dark:bg-slate-900">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-black tracking-tight flex items-center gap-2 text-slate-900 dark:text-slate-100">
                    <History className="w-5 h-5 text-primary" />
                    Historique de paiement
                  </h3>
                  <Button variant="outline" size="sm" className="rounded-xl border-slate-200 font-bold" onClick={() => setNotifDialog(true)}>
                    Envoyer un rappel
                  </Button>
                </div>

                {loadingDetails ? (
                  <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary/50" /></div>
                ) : memberContributions.length > 0 ? (
                  <div className="space-y-4">
                    {memberContributions.map((c) => (
                      <div key={c.id} className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex items-center justify-between group hover:border-primary/20 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.status === 'confirmed' ? 'bg-emerald-100 text-emerald-600' :
                            c.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
                            }`}>
                            <Wallet className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 dark:text-slate-100">{c.type || 'Cotisation Mensuelle'}</p>
                            <p className="text-xs text-slate-500 font-medium">
                              {new Date(c.createdAt?.toDate?.() || c.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                              {c.paymentMethod && ` • via ${c.paymentMethod.toUpperCase()}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-black text-lg ${c.status === 'confirmed' ? 'text-emerald-500' : 'text-slate-400'}`}>
                            {c.amount?.toLocaleString()} F
                          </p>
                          <div className="flex items-center justify-end gap-1.5 pt-0.5">
                            {c.status === 'confirmed' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />}
                            <span className={`text-[10px] font-black uppercase tracking-widest ${c.status === 'confirmed' ? 'text-emerald-500' :
                              c.status === 'rejected' ? 'text-red-500' : 'text-amber-500'
                              }`}>
                              {c.status === 'confirmed' ? 'Confirmé' : c.status === 'rejected' ? 'Refusé' : 'En attente'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 bg-white dark:bg-slate-800/50 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800 text-center">
                    <MailQuestion className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 font-bold">Aucune transaction enregistrée pour ce membre.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* INDIVIDUAL NOTIFICATION DIALOG */}
      <Dialog open={notifDialog} onOpenChange={setNotifDialog}>
        <DialogContent className="max-w-md rounded-[2.5rem] p-0 overflow-hidden border-0 shadow-2xl">
          <form onSubmit={handleSendNotification}>
            <div className="p-8 bg-gradient-to-br from-primary to-emerald-600 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/20">
                  <Bell className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-black tracking-tight">Notifier le membre</h3>
                  <p className="text-white/70 text-sm">Le message sera envoyé instantanément.</p>
                </div>
              </div>
              <div className="p-3 bg-black/10 rounded-2xl border border-white/10 backdrop-blur-sm">
                <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1">Destinataire</p>
                <p className="font-bold flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-300" />
                  {selectedMember?.fullName}
                </p>
              </div>
            </div>

            <div className="p-8 space-y-5 bg-white dark:bg-slate-900 border-b border-slate-100">
              <div className="space-y-2">
                <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Objet de l'alerte</Label>
                <Input
                  value={notifTitle}
                  onChange={(e) => setNotifTitle(e.target.value)}
                  placeholder="Ex: Rappel de cotisation"
                  className="rounded-xl border-slate-200 h-11 focus:ring-primary/20"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-black uppercase tracking-widest text-slate-400">Message personnalisé</Label>
                <Textarea
                  value={notifMessage}
                  onChange={(e) => setNotifMessage(e.target.value)}
                  placeholder="Écrivez votre message ici..."
                  className="rounded-2xl border-slate-200 focus:ring-primary/20 min-h-[140px]"
                  required
                />
              </div>
            </div>

            <DialogFooter className="p-6 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3 rounded-b-[2.5rem]">
              <Button type="button" variant="ghost" onClick={() => setNotifDialog(false)} className="rounded-2xl font-bold h-12">
                Annuler
              </Button>
              <Button type="submit" disabled={sendingNotif} className="bg-primary hover:bg-primary/90 text-white shadow-xl shadow-primary/20 rounded-2xl h-12 px-8 font-bold">
                {sendingNotif ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Bell className="w-4 h-4 mr-2" /> Envoyer l'alerte</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ban Member Confirmation Modal */}
      <ConfirmDeleteDialog
        open={!!memberToBan}
        onOpenChange={(open) => !open && setMemberToBan(null)}
        onConfirm={handleBanMember}
        title="Bannir ce membre ?"
        description="Cette action est irréversible. Le membre sera définitivement supprimé du Dahira et ne pourra plus accéder à son compte."
        itemName={memberToBan?.fullName}
        confirmText="Bannir du Dahira"
        loading={banning}
        variant="danger"
      />

      {/* Dialog pour modifier le Sass Annuel */}
      <Dialog open={editingGoalDialog} onOpenChange={setEditingGoalDialog}>
        <DialogContent className="max-w-md rounded-[2.5rem] p-0 overflow-hidden border-0 shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="p-8 bg-gradient-to-br from-emerald-600 to-green-700 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/20">
                  <Target className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-black tracking-tight">Sass Annuel</h3>
                  <p className="text-white/70 text-sm">Objectif de cotisation annuel</p>
                </div>
              </div>
              <div className="p-3 bg-black/10 rounded-2xl border border-white/10 backdrop-blur-sm">
                <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-1">Membre</p>
                <p className="font-bold flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-300" />
                  {selectedMember?.fullName}
                </p>
              </div>
            </div>

            <div className="p-8 space-y-5 bg-white dark:bg-slate-900">
              {/* Catégorie Sass (profil) */}
              <div className="space-y-2">
                <Label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Catégorie (profil)
                </Label>
                <div className="grid grid-cols-1 gap-2">
                  {sassCategories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => applySassCategory(cat)}
                      className={`flex items-center justify-between p-3 rounded-2xl border-2 text-left transition-all ${selectedSassCategory === cat.id
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'}`}
                    >
                      <span className="font-bold text-slate-900 dark:text-slate-100">{cat.label}</span>
                      <span className="font-mono font-bold text-emerald-600">{cat.amount.toLocaleString()} F</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 italic text-center">
                  Choisir un profil applique automatiquement son montant. Configurable dans les Réglages.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Montant annuel cible (FCFA)
                </Label>
                <Input
                  type="number"
                  value={memberAnnualGoal}
                  onChange={(e) => { setMemberAnnualGoal(e.target.value); setSelectedSassCategory(''); }}
                  placeholder={String(settings.annualGoal || 100000)}
                  className="rounded-xl border-slate-200 h-14 text-2xl font-bold text-center focus:ring-emerald-500/20"
                />
                <p className="text-xs text-slate-400 text-center">
                  Par défaut: {(settings.annualGoal || 100000).toLocaleString()} FCFA
                </p>
              </div>

              {/* Montants suggérés */}
              <div className="space-y-2">
                <Label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Suggestions
                </Label>
                <div className="grid grid-cols-4 gap-2">
                  {[50000, 100000, 150000, 200000].map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={Number(memberAnnualGoal) === amount ? "default" : "outline"}
                      size="sm"
                      onClick={() => { setMemberAnnualGoal(amount); setSelectedSassCategory(''); }}
                      className="rounded-xl text-xs"
                    >
                      {(amount / 1000)}k
                    </Button>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-emerald-800 dark:text-emerald-200">
                      {Number(memberAnnualGoal).toLocaleString()} FCFA / an
                    </p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      ≈ {Math.round(Number(memberAnnualGoal) / 12).toLocaleString()} FCFA / mois
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div> {/* This is the added closing div tag */}

          <DialogFooter className="p-6 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3 rounded-b-[2.5rem] shrink-0 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditingGoalDialog(false)}
              className="rounded-2xl font-bold h-12"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveAnnualGoal}
              disabled={savingGoal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl shadow-emerald-500/20 rounded-2xl h-12 px-8 font-bold"
            >
              {savingGoal ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Target className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
